package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.MerchantLocationDetailsUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep7
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.toLegacyWhatYouCouldGetString
import kotlinx.coroutines.launch

/**
 * Entry point for location details/edit: loads the location and immediately
 * navigates into the same multi-screen flow used for creating a location
 * (Step7 → Step8 → Step9 → Step9B), with data pre-filled. User sees
 * a short loading state then the first step of the flow.
 */
class MerchantLocationDetailsViewModel(
    context: AppContext,
    private val locationId: Long
) : ChildViewModel<MerchantLocationDetailsUIState>(context, MerchantLocationDetailsUIState()) {

    override fun onCreate() {
        navControls.title = sharedString(SR.strings.location_details)
        navControls.backButtonVisible = true
        loadLocationAndNavigateToEditFlow()
    }

    private fun loadLocationAndNavigateToEditFlow() {
        scope.launch {
            updateState { it.copy(isLoading = true) }

            commandBlock(showProgress = false, hideProgress = false) {
                val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
                if (merchantId == 0L) {
                    launchOnMain { updateState { it.copy(isLoading = false) } }
                    return@commandBlock
                }

                CommonCommands.getMerchantLocations(GetMerchantLocationsRequestModel(merchantId = merchantId))
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
                                val location = responseModel?.locations?.find { it.id == locationId }
                                if (location != null) {
                                    updateState { it.copy(isLoading = false) }
                                    val step7Config = NavigatorMerchantRegistrationStep7(
                                        merchantId = merchantId,
                                        storeName = location.title ?: "",
                                        storeAddress = location.address ?: "",
                                        cityId = location.cityId.takeIf { it > 0L },
                                        cityName = location.cityName ?: "",
                                        storePhoneNumber = location.phoneNumber,
                                        dialCode = location.dialCode,
                                        packaging = location.packaging ?: "",
                                        additionalPickupInstructions = location.additionalPickupInstructions ?: "",
                                        surpriseBagContainsBuffetFood = location.surpriseBagContainsBuffetFood,
                                        returnToDashboard = false,
                                        locationId = location.id,
                                        initialLatitude = location.latitude.toDouble().takeUnless { location.latitude == -1f },
                                        initialLongitude = location.longitude.toDouble().takeUnless { location.longitude == -1f },
                                        whatYouCouldGet = location.products.toLegacyWhatYouCouldGetString(),
                                        businessCategoryId = location.businessCategoryId,
                                        existingCoverPhotoUrl = location.coverPhotoUrl?.takeIf { it.isNotBlank() },
                                        existingMenuPhotoUrls = location.menuPhotoUrls ?: emptyList()
                                    )
                                    goBack {
                                        navigate(step7Config)
                                    }
                                } else {
                                    updateState { it.copy(isLoading = false) }
                                }
                            }
                        },
                        baseError = {
                            launchOnMain { updateState { it.copy(isLoading = false) } }
                        }
                    )
            }
        }
    }
}
